<template>
  <div>
    <v-row
      v-if="!showEditSignature && mutableSignatures.length === 0"
      justify="center"
      align="center"
    >
      <v-col cols="1">
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          outlined
          @click="addSignature"
        >
          {{ $t("Add") }}
        </v-btn>
      </v-col>
    </v-row>
    <div
      v-for="(signature) in mutableSignatures"
      v-if="!showEditSignature"
      :key="signature.uuid"
    >
      <v-row align="center">
        <v-col cols="2">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                large
                color="error"
                icon
                @click="deleteSignature(signature)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </v-col>
        <v-col cols="5">
          <v-text-field
            readonly
            v-model="signature.description"
            :label="$t('Description')"
          ></v-text-field>
        </v-col>
        <v-col cols="auto">
          <v-radio-group
            v-model="signature.isDefault"
            column
          >
            <v-radio
              :readonly="signature.isDefault"
              :value="true"
              v-bind:label="$t('IsDefault')"
              @change="defaultSignatureChanged(signature)"
            ></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="1">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <a
                v-on="on"
                @click.prevent="editSignature(signature)"
                class="px-2"
              >
                <v-icon color="primary">mdi-pencil</v-icon>
              </a>
            </template>
            <span>{{ $t("Edit") }}</span>
          </v-tooltip>
        </v-col>
        <v-col cols="1">
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <a v-on="on" @click.prevent="addSignature" class="px-2">
                <v-icon color="primary">mdi-plus</v-icon>
              </a>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <UserSignature
      :ref="userSignatureKey"
      :onCancel="showTableAgain"
      :onValidate="validateSignatureUniqueness"
      v-show="showEditSignature"
      @saveSignature="saveSignature"
    ></UserSignature>
  </div>
</template>
<script>
import UserSignature from './UserSignature';
import UserService from '@/services/UserService';
import Utils from '@/ecm/common/Utils';
import Signature from '../../../ecm/models/Signature';

export default {
    components: { UserSignature },
    name: 'UserSignaturesList',
    props: {
        signatures: Array,
        user: Object,
    },
    computed: {
        userSignatureInstance() {
            return this.$refs[this.userSignatureKey];
        },
    },
    data() {
        return {
            showEditSignature: false,
            mutableSignatures: this.signatures,
            userSignatureKey: 'userSignature',
        };
    },
    methods: {
        addSignature() {
            this.showEditSignature = true;

            this.userSignatureInstance.setSignatureForEdit(new Signature());
        },
        editSignature(signature) {
            if (!signature.signatureImage) {
                UserService.getSignatureImage(this.user.id, signature.id).then(
                    (result) => {
                        if (result) {
                            signature.signatureImage = Utils.file.image.toBase64(result);

                            this.displaySignatureInformation(signature);
                        }
                    }
                );
            } else {
                this.displaySignatureInformation(signature);
            }
        },
        defaultSignatureChanged(signature) {
            this.mutableSignatures.forEach((mutableSignature) => {
                if (mutableSignature.id != signature.id && mutableSignature.isDefault) {
                    mutableSignature.isDefault = false;
                }
            });

            this.$emit('signaturesChanged', this.mutableSignatures);
        },
        displaySignatureInformation(signature) {
            this.$nextTick(() => {
                const clonedSignature = { ...signature };
                Object.setPrototypeOf(clonedSignature, signature);

                this.userSignatureInstance.setSignatureForEdit(clonedSignature);
                this.showEditSignature = true;
            });
        },
        deleteSignature(signature) {
            this.mutableSignatures = this.mutableSignatures.filter(
                (p) => p.id !== signature.id
            );
            if (signature.isDefault && this.mutableSignatures.length > 0) {
                this.mutableSignatures[0].isDefault = true;
            }
            this.$emit('signaturesChanged', this.mutableSignatures);
        },
        showTableAgain() {
            this.showEditSignature = false;
        },
        validateSignatureUniqueness(tentativeSignature) {
            if (
                this.mutableSignatures.some(
                    (signature) =>
                        signature.id !== tentativeSignature.id &&
                        signature.description === tentativeSignature.description
                )
            ) {
                Utils.toast.error(
                    this.$t('EverySignatureShouldHaveADifferentDescriptionException')
                );

                return false;
            }

            if (
                !tentativeSignature.isDefault &&
                this.mutableSignatures.every((signature) => !signature.isDefault)
            ) {
                Utils.toast.error(
                    this.$t('YouNeedToSetOneSignatureAsDefaultException')
                );

                return false;
            }

            return true;
        },
        saveSignature(signature) {
            if (signature.isDefault) {
                this.mutableSignatures.forEach(
                    (signature) => (signature.isDefault = false)
                );
            }

            if (signature.id === 0) {
                this.generateNewTemporaryId(signature);
                this.mutableSignatures.push(signature);
            } else {
                this.mutableSignatures = this.mutableSignatures.map((u) =>
                    u.id !== signature.id ? u : signature
                );
            }

            this.showEditSignature = false;
            this.$emit('signaturesChanged', this.mutableSignatures);
        },
        generateNewTemporaryId(signature) {
            do {
                const rndId = Math.floor(Math.random() * 2147483647);

                signature.id = -1 * rndId;
            } while (this.mutableSignatures.some((p) => p.id === signature.id));
        },
    },
    watch: {
        signatures(newVal, oldVal) {
            this.showEditSignature = false;
            this.mutableSignatures = newVal;
        },
    },
};
</script>
